import React, { useState } from "react";
import { t } from '../../../i18n';
import { Query, Mutation } from "react-apollo";
import queries from "../../../queries";
import { FormattedMessage, injectIntl } from "react-intl";
import PaletteColorPicker from "../../PaletteColorPicker";
import {withRouter} from "react-router-dom";
import PhotoboothCellStatsButton from "../PhotoboothCellStatsButton";
import PhotoboothCellPhotosZipButton from "../PhotoboothCellPhotosZipButton";
import EditorSection from "./EditorSection";
import ImageUploader from "../../uploaders/ImageUploader";
import confirmDialog from "../../dialogs/ConfirmDialog";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

// The picker maps each filter to the OBS sources bg_<slot> / frame_<slot>, and the booth only ever
// gets 10 layers built by hand.
const SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function PhotoboothOBSCellEditorWrapper({...props}) {

  const {projectId} = props.match.params;

  const cell = props.cell || props.initialCellData;

  const language = props.language;

  if (!cell) {
    return <p>No cell data provided.</p>;
  }

  return (
    <Query query={queries.cells.photobooth_obs.get} variables={{ id: cell.id }}>
      {({ loading, error, data }) => {
        if (loading) return <p>{t('ui.common.loading')}</p>;
        if (error) return <p>Error: {error.message}</p>;
        if (!data) return null;
        if(!!language) {
          return <PhotoboothOBSCellEditorInner projectId={projectId} language={language} initialCellData={{...data.photoboothOBSCell, language}} />;
        }
        return <PhotoboothOBSCellEditorInner projectId={projectId} initialCellData={data.photoboothOBSCell} />;
      }}
    </Query>
  );
}

const PhotoboothOBSCellEditorInner = injectIntl(function PhotoboothOBSCellEditorInnerBase({ initialCellData, projectId, language, intl }) {
  const [cellData, setCellData] = useState(initialCellData);

  if (!cellData) {
    return <p>No cell data provided.</p>;
  }

  return (
    <Mutation
      mutation={language ? queries.cells.i18n.update : queries.cells.photobooth_obs.update}
      refetchQueries={[
        { query: queries.cells.photobooth_obs.get, variables: { id: cellData.id } },
      ]}
    >
      {(updateCell, { loading: mutationLoading, error: mutationError }) => (
        <>
          {mutationError && <p>Error: {mutationError.message}</p>}

          {/* You can use the additional data here if you need it */}
          <Query query={queries.projects.getWithChildren} variables={{ id: projectId }}>
            {({ loading, error, data }) => {
              if (loading) return <p>{t('ui.common.loading')}</p>;
              if (error) return <p>Error loading project: {error.message}</p>;
              if (!data) return null;

              const {project} = data;
              // Use data.project here if needed, but don't re-render the component
              return (
                <div className="tsk-editor-form">
                  {!language && (
                    <>
                      <EditorSection title={<FormattedMessage id="projects.photobooth.gif.cell.colors_section" />}>
                        <EditorSection nested title={<FormattedMessage id="projects.photobooth.gif.cell.colors_section.background" />}>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.background_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.background_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, background_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.button_download_background_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.button_download_background_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, button_download_background_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.button_retake_background_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.button_retake_background_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, button_retake_background_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.button_print_background_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.button_print_background_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, button_print_background_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.qr_card_background_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.qr_card_background_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, qr_card_background_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.loader_background_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.loader_background_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, loader_background_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                        </EditorSection>

                        <EditorSection nested title={<FormattedMessage id="projects.photobooth.gif.cell.colors_section.text" />}>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.text_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.text_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, text_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.button_download_text_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.button_download_text_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, button_download_text_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.button_retake_text_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.button_retake_text_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, button_retake_text_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                        </EditorSection>

                        <EditorSection nested title={<FormattedMessage id="projects.photobooth.gif.cell.colors_section.other" />}>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.button_retake_border_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.button_retake_border_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, button_retake_border_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.qr_code_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.qr_code_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, qr_code_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.loader_spinner_color" /></label>
                            <PaletteColorPicker
                              palette={project.color_palette}
                              color={cellData.loader_spinner_color}
                              onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, loader_spinner_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                            />
                          </div>
                        </EditorSection>
                      </EditorSection>

                      <EditorSection defaultOpen={false} title={<FormattedMessage id="projects.photobooth.gif.cell.text_tool_section" />}>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="projects.photobooth.gif.cell.text_font" /></label>
                          <select
                            value={cellData.text_font || ''}
                            onChange={(e) => setCellData({ ...cellData, text_font: e.target.value })}
                          >
                            <option value="">{intl.formatMessage({ id: "cell.button.default.label" })}</option>
                            {project.fonts.map((f) => <option key={f.id} value={f.name}>{f.name}</option>)}
                          </select>
                        </div>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="projects.photobooth.gif.cell.text_size" /></label>
                          <input
                            type="number"
                            step={0.1}
                            min={0.1}
                            value={cellData.text_size != null ? cellData.text_size : 1}
                            onChange={(e) => {
                              const parsed = parseFloat(e.target.value);
                              setCellData({ ...cellData, text_size: Number.isNaN(parsed) ? null : parsed });
                            }}
                          />
                        </div>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="projects.photobooth.gif.cell.letter_spacing" /></label>
                          <input
                            type="number"
                            step={0.5}
                            value={cellData.letter_spacing != null ? cellData.letter_spacing : 0}
                            onChange={(e) => {
                              const parsed = parseFloat(e.target.value);
                              setCellData({ ...cellData, letter_spacing: Number.isNaN(parsed) ? null : parsed });
                            }}
                          />
                        </div>
                      </EditorSection>

                      <EditorSection defaultOpen={false} title={<FormattedMessage id="projects.photobooth.gif.cell.share_section" />}>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="Share.Email" /></label>
                          <input
                            type="checkbox"
                            value={cellData.email}
                            checked={cellData.email}
                            onChange={(e) => setCellData({ ...cellData, email: e.target.checked })}
                          />
                        </div>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="Share.Qr_Code" /></label>
                          <input
                            type="checkbox"
                            value={cellData.qr_code}
                            checked={cellData.qr_code}
                            onChange={(e) => setCellData({ ...cellData, qr_code: e.target.checked })}
                          />
                        </div>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="Share.Print" /></label>
                          <input
                            type="checkbox"
                            value={cellData.print}
                            checked={cellData.print}
                            onChange={(e) => setCellData({ ...cellData, print: e.target.checked })}
                          />
                        </div>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="Share.cameralabel" /></label>
                          <input
                            type="text"
                            value={cellData.cameralabel}
                            onChange={(e) => setCellData({ ...cellData, cameralabel: e.target.value })}
                          />
                        </div>
                      </EditorSection>

                      <EditorSection defaultOpen={false} title={<FormattedMessage id="projects.photobooth.gif.cell.photo_format" />}>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="projects.photobooth.gif.cell.photo_format" /></label>
                          <select
                            value={cellData.photo_format || 'default'}
                            onChange={(e) => setCellData({ ...cellData, photo_format: e.target.value })}
                          >
                            <option value="default">10x15</option>
                            <option value="square">15x15</option>
                          </select>
                        </div>
                        {(cellData.photo_format === 'default' || !cellData.photo_format) && (
                          <div className="tsk-input-line">
                            <label><FormattedMessage id="projects.photobooth.gif.cell.panini_print" /></label>
                            <input
                              type="checkbox"
                              value={cellData.panini_print}
                              checked={cellData.panini_print}
                              onChange={(e) => setCellData({ ...cellData, panini_print: e.target.checked })}
                            />
                          </div>
                        )}
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="projects.photobooth.gif.cell.camera_rotation" /></label>
                          <select
                            value={cellData.camera_rotation != null ? cellData.camera_rotation : 0}
                            onChange={(e) => setCellData({ ...cellData, camera_rotation: parseInt(e.target.value, 10) })}
                          >
                            <option value={0}>0°</option>
                            <option value={90}>90°</option>
                            <option value={180}>180°</option>
                            <option value={270}>270°</option>
                          </select>
                        </div>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="projects.photobooth.gif.cell.mirror_image" /></label>
                          <input
                            type="checkbox"
                            value={cellData.mirror_image}
                            checked={cellData.mirror_image}
                            onChange={(e) => setCellData({ ...cellData, mirror_image: e.target.checked })}
                          />
                        </div>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="projects.photobooth.obs.cell.change_bg_button_position" /></label>
                          <input
                            type="number"
                            step={1}
                            min={0}
                            max={100}
                            value={cellData.change_bg_button_position != null ? cellData.change_bg_button_position : 8}
                            onChange={(e) => {
                              const parsed = parseInt(e.target.value, 10);
                              setCellData({ ...cellData, change_bg_button_position: Number.isNaN(parsed) ? null : parsed });
                            }}
                          />
                        </div>
                        {/* Background removal + foreground frame are composited in OBS for this cell,
                            so no remove_background / background image / frame (gif) controls here. */}
                      </EditorSection>

                      {/* Filtres OBS: the picker list used to live in the booth's config.json. Rows are
                          edited here and saved with everything else by the Update button below. `slot` is
                          explicit (not the row position) because it names the OBS sources bg_<slot> /
                          frame_<slot> — deleting a row must never renumber the others. */}
                      <EditorSection defaultOpen={false} title={<FormattedMessage id="projects.photobooth.obs.cell.filters_section" />}>
                        {(() => {
                          const filters = [...(cellData.obs_filters || [])].sort((a, b) => a.slot - b.slot);
                          // Apollo deep-freezes query results in dev, so every edit rebuilds the array.
                          const setFilters = (next) => setCellData({ ...cellData, obs_filters: next });
                          const usedSlots = filters.map((f) => f.slot);
                          const freeSlot = SLOTS.find((n) => !usedSlots.includes(n));
                          return (
                            <>
                              {filters.length === 0 ? (
                                <p><FormattedMessage id="projects.photobooth.obs.cell.filters_empty" /></p>
                              ) : (
                                <p><FormattedMessage id="projects.photobooth.obs.cell.filter_images_hint" /></p>
                              )}
                              {filters.map((filter, i) => (
                                <div key={filter.slot} className="tsk-input-line tsk-input-line--full">
                                  <label>{`bg_${filter.slot} / frame_${filter.slot}`}</label>
                                  {/* Three separate images per filter: the small picker tile, and the two
                                      full-res files the kiosk pushes into the OBS sources bg_N (behind the
                                      person) and frame_N (transparent PNG in front of them). Both OBS ones
                                      are optional — an empty one leaves that source as it is in OBS.
                                      ImageUploader has no label prop, and three bare dropzones in a row are
                                      indistinguishable — hence the wrapper + caption on each. */}
                                  <div>
                                    <label><FormattedMessage id="projects.photobooth.obs.cell.filter_thumbnail" /></label>
                                    <ImageUploader
                                      value={filter.image_uri}
                                      onUpload={(uri) => setFilters(filters.map((f, j) => (j === i ? { ...f, image_uri: uri } : f)))}
                                    />
                                  </div>
                                  <div>
                                    <label>
                                      <FormattedMessage id="projects.photobooth.obs.cell.filter_background" />
                                      {` (bg_${filter.slot})`}
                                    </label>
                                    <ImageUploader
                                      value={filter.background_uri}
                                      onUpload={(uri) => setFilters(filters.map((f, j) => (j === i ? { ...f, background_uri: uri } : f)))}
                                    />
                                  </div>
                                  <div>
                                    <label>
                                      <FormattedMessage id="projects.photobooth.obs.cell.filter_overlay" />
                                      {` (frame_${filter.slot})`}
                                    </label>
                                    <ImageUploader
                                      value={filter.overlay_uri}
                                      onUpload={(uri) => setFilters(filters.map((f, j) => (j === i ? { ...f, overlay_uri: uri } : f)))}
                                    />
                                  </div>
                                  {/* No background uploaded already means "don't cut the person out"
                                      (there is nothing to put behind them). This forces the same when
                                      the removal is poor on a given decor. */}
                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={!!filter.keep_background}
                                      onChange={(e) => setFilters(filters.map((f, j) => (j === i ? { ...f, keep_background: e.target.checked } : f)))}
                                    />
                                    <FormattedMessage id="projects.photobooth.obs.cell.filter_keep_background" />
                                  </label>
                                  <input
                                    type="text"
                                    placeholder={intl.formatMessage({ id: 'projects.photobooth.obs.cell.filter_name' })}
                                    value={filter.name || ''}
                                    onChange={(e) => setFilters(filters.map((f, j) => (j === i ? { ...f, name: e.target.value } : f)))}
                                  />
                                  <select
                                    value={filter.slot}
                                    onChange={(e) => {
                                      const slot = parseInt(e.target.value, 10);
                                      setFilters(filters.map((f, j) => (j === i ? { ...f, slot } : f)));
                                    }}
                                  >
                                    {SLOTS.filter((n) => n === filter.slot || !usedSlots.includes(n)).map((n) => (
                                      <option key={n} value={n}>{n}</option>
                                    ))}
                                  </select>
                                  <span
                                    style={{ cursor: 'pointer' }}
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      await confirmDialog(
                                        <FormattedMessage id="projects.photobooth.obs.cell.filter_delete_confirm" />
                                      ).then(() => setFilters(filters.filter((f, j) => j !== i)));
                                    }}
                                  >
                                    <FontAwesomeIcon icon={'trash-alt'} />
                                  </span>
                                </div>
                              ))}
                              {freeSlot != null && (
                                <button
                                  className="tsk-button"
                                  onClick={() => setFilters([...filters, { slot: freeSlot, name: '', image_uri: null, background_uri: null, overlay_uri: null, keep_background: false }])}
                                >
                                  <FormattedMessage id="projects.photobooth.obs.cell.filter_add" />
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </EditorSection>

                      <EditorSection defaultOpen={false} title={<FormattedMessage id="projects.photobooth.gif.cell.timer_section" />}>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="projects.photobooth.gif.cell.photo_timer" /></label>
                          <input
                            type="number"
                            min={1}
                            max={10}
                            value={cellData.photo_timer != null ? cellData.photo_timer : 3}
                            onChange={(e) => {
                              const parsed = parseInt(e.target.value, 10);
                              const next = Number.isNaN(parsed) ? '' : Math.min(10, Math.max(1, parsed));
                              setCellData({ ...cellData, photo_timer: next });
                            }}
                          />
                        </div>
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="projects.photobooth.gif.cell.photo_timer_direction" /></label>
                          <select
                            value={cellData.photo_timer_direction || 'desc'}
                            onChange={(e) => setCellData({ ...cellData, photo_timer_direction: e.target.value })}
                          >
                            <option value="desc">3, 2, 1</option>
                            <option value="asc">1, 2, 3</option>
                          </select>
                        </div>
                      </EditorSection>

                      <EditorSection
                        title={<FormattedMessage id="projects.photobooth.gif.cell.collect_user_info" />}
                        defaultOpen={false}
                      >
                        <div className="tsk-input-line">
                          <label><FormattedMessage id="projects.photobooth.gif.cell.collect_user_info" /></label>
                          <input
                            type="checkbox"
                            value={cellData.collect_user_info}
                            checked={cellData.collect_user_info}
                            onChange={(e) => setCellData({ ...cellData, collect_user_info: e.target.checked })}
                          />
                        </div>
                        {cellData.collect_user_info && (
                          <>
                            <div className="tsk-input-line">
                              <label><FormattedMessage id="projects.photobooth.gif.cell.user_info_bar_color" /></label>
                              <PaletteColorPicker
                                palette={project.color_palette}
                                color={cellData.user_info_bar_color}
                                onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, user_info_bar_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                              />
                            </div>
                            <div className="tsk-input-line">
                              <label><FormattedMessage id="projects.photobooth.gif.cell.user_info_text_color" /></label>
                              <PaletteColorPicker
                                palette={project.color_palette}
                                color={cellData.user_info_text_color}
                                onChange={(val) => {const {rgb} = val; setCellData({ ...cellData, user_info_text_color: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` })}}
                              />
                            </div>
                            <div className="tsk-input-line">
                              <label><FormattedMessage id="projects.photobooth.gif.cell.user_info_text_size" /></label>
                              <input
                                type="range"
                                min={0.5}
                                max={2}
                                step={0.1}
                                value={cellData.user_info_text_size != null ? cellData.user_info_text_size : 1}
                                onChange={(e) => setCellData({ ...cellData, user_info_text_size: parseFloat(e.target.value) })}
                              />
                              <span>{(cellData.user_info_text_size != null ? cellData.user_info_text_size : 1).toFixed(1)}x</span>
                            </div>
                            <div className="tsk-input-line">
                              <label><FormattedMessage id="projects.photobooth.gif.cell.user_info_text_position" /></label>
                              <input
                                type="range"
                                min={2}
                                max={40}
                                step={1}
                                value={cellData.user_info_text_position != null ? cellData.user_info_text_position : 12}
                                onChange={(e) => setCellData({ ...cellData, user_info_text_position: parseInt(e.target.value, 10) })}
                              />
                              <span>{cellData.user_info_text_position != null ? cellData.user_info_text_position : 12}%</span>
                            </div>
                            <div className="tsk-input-line">
                              <label><FormattedMessage id="projects.photobooth.gif.cell.user_info_text_h_position" /></label>
                              <input
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                value={cellData.user_info_text_h_position != null ? cellData.user_info_text_h_position : 50}
                                onChange={(e) => setCellData({ ...cellData, user_info_text_h_position: parseInt(e.target.value, 10) })}
                              />
                              <span>{cellData.user_info_text_h_position != null ? cellData.user_info_text_h_position : 50}%</span>
                            </div>
                          </>
                        )}
                      </EditorSection>
                    </>
                  )}

                  <EditorSection defaultOpen={false} title={<FormattedMessage id="projects.photobooth.gif.cell.description_text" />}>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.button_take_text" /></label>
                      <input
                        type="text"
                        value={cellData.button_take_text}
                        onChange={(e) => setCellData({ ...cellData, button_take_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.button_retake_text" /></label>
                      <input
                        type="text"
                        value={cellData.button_retake_text}
                        onChange={(e) => setCellData({ ...cellData, button_retake_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.button_download_text" /></label>
                      <input
                        type="text"
                        value={cellData.button_download_text}
                        onChange={(e) => setCellData({ ...cellData, button_download_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.send_text" /></label>
                      <input
                        type="text"
                        value={cellData.send_text}
                        onChange={(e) => setCellData({ ...cellData, send_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.sent_text" /></label>
                      <input
                        type="text"
                        value={cellData.sent_text}
                        onChange={(e) => setCellData({ ...cellData, sent_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.look_text" /></label>
                      <input
                        type="text"
                        value={cellData.look_text}
                        onChange={(e) => setCellData({ ...cellData, look_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.description_text" /></label>
                      <input
                        type="text"
                        value={cellData.description_text}
                        onChange={(e) => setCellData({ ...cellData, description_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.conditions1_text" /></label>
                      <input
                        type="text"
                        value={cellData.conditions1_text}
                        onChange={(e) => setCellData({ ...cellData, conditions1_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.conditions2_text" /></label>
                      <input
                        type="text"
                        value={cellData.conditions2_text}
                        onChange={(e) => setCellData({ ...cellData, conditions2_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.send_by_email_text" /></label>
                      <input
                        type="text"
                        value={cellData.send_by_email_text || ''}
                        onChange={(e) => setCellData({ ...cellData, send_by_email_text: e.target.value })}
                      />
                    </div>
                    {/* Progress screen shown while the photo is processed: first message, then the
                        second. Blank falls back to the built-in per-language string. */}
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.preparing_photo_text" /></label>
                      <input
                        type="text"
                        value={cellData.preparing_photo_text || ''}
                        onChange={(e) => setCellData({ ...cellData, preparing_photo_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.almost_there_text" /></label>
                      <input
                        type="text"
                        value={cellData.almost_there_text || ''}
                        onChange={(e) => setCellData({ ...cellData, almost_there_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.print_text" /></label>
                      <input
                        type="text"
                        value={cellData.print_text || ''}
                        onChange={(e) => setCellData({ ...cellData, print_text: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.gif.cell.printed_text" /></label>
                      <input
                        type="text"
                        value={cellData.printed_text || ''}
                        onChange={(e) => setCellData({ ...cellData, printed_text: e.target.value })}
                      />
                    </div>
                    {/* Mail envoyé depuis l'écran de partage. Champ vide = texte par défaut. */}
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.obs.cell.email_subject" /></label>
                      <input
                        type="text"
                        value={cellData.email_subject || ''}
                        onChange={(e) => setCellData({ ...cellData, email_subject: e.target.value })}
                      />
                    </div>
                    <div className="tsk-input-line">
                      <label><FormattedMessage id="projects.photobooth.obs.cell.email_body" /></label>
                      <textarea
                        rows={5}
                        value={cellData.email_body || ''}
                        onChange={(e) => setCellData({ ...cellData, email_body: e.target.value })}
                      />
                    </div>
                    {!language && (
                      <div className="tsk-input-line">
                        <label><FormattedMessage id="projects.photobooth.obs.cell.email_from" /></label>
                        <input
                          type="text"
                          value={cellData.email_from || ''}
                          onChange={(e) => setCellData({ ...cellData, email_from: e.target.value })}
                        />
                      </div>
                    )}
                  </EditorSection>
                </div>
              );
            }}
          </Query>
          <button
            className="tsk-button"
            onClick={() => updateCell({ variables: { input: removeTypenameDeep(!language ? {
              id: cellData.id,
              send_email: cellData.send_email,
              background_color: cellData.background_color,
              text_color: cellData.text_color,
              button_download_background_color: cellData.button_download_background_color,
              button_download_text_color: cellData.button_download_text_color,
              button_retake_background_color: cellData.button_retake_background_color,
              button_retake_border_color: cellData.button_retake_border_color,
              button_retake_text_color: cellData.button_retake_text_color,
              button_print_background_color: cellData.button_print_background_color,
              qr_card_background_color: cellData.qr_card_background_color,
              qr_code_color: cellData.qr_code_color,
              loader_background_color: cellData.loader_background_color,
              loader_spinner_color: cellData.loader_spinner_color,
              button_download_text: cellData.button_download_text,
              button_retake_text: cellData.button_retake_text,
              button_take_text: cellData.button_take_text,
              send_text: cellData.send_text,
              sent_text: cellData.sent_text,
              look_text: cellData.look_text,
              description_text: cellData.description_text,
              conditions1_text: cellData.conditions1_text,
              conditions2_text: cellData.conditions2_text,
              send_by_email_text: cellData.send_by_email_text,
              print_text: cellData.print_text,
              printed_text: cellData.printed_text,
              preparing_photo_text: cellData.preparing_photo_text,
              almost_there_text: cellData.almost_there_text,
              print: cellData.print,
              cameralabel: cellData.cameralabel,
              qr_code: cellData.qr_code,
              email: cellData.email,
              mirror_image: cellData.mirror_image,
              photo_format: cellData.photo_format,
              photo_timer: cellData.photo_timer === '' || cellData.photo_timer == null ? null : parseInt(cellData.photo_timer, 10),
              photo_timer_direction: cellData.photo_timer_direction,
              panini_print: cellData.panini_print,
              camera_rotation: cellData.camera_rotation == null ? 0 : parseInt(cellData.camera_rotation, 10),
              collect_user_info: !!cellData.collect_user_info,
              user_info_bar_color: cellData.user_info_bar_color,
              user_info_text_color: cellData.user_info_text_color,
              user_info_text_size: cellData.user_info_text_size == null ? 1 : parseFloat(cellData.user_info_text_size),
              user_info_text_position: cellData.user_info_text_position == null ? 12 : parseInt(cellData.user_info_text_position, 10),
              user_info_text_h_position: cellData.user_info_text_h_position == null ? 50 : parseInt(cellData.user_info_text_h_position, 10),
              text_font: cellData.text_font || null,
              text_size: cellData.text_size == null ? null : parseFloat(cellData.text_size),
              letter_spacing: cellData.letter_spacing == null ? null : parseFloat(cellData.letter_spacing),
              change_bg_button_position: cellData.change_bg_button_position == null || cellData.change_bg_button_position === '' ? null : parseInt(cellData.change_bg_button_position, 10),
              obs_filters: cellData.obs_filters || [],
              email_subject: cellData.email_subject,
              email_body: htmlLineBreaks(cellData.email_body),
              email_from: cellData.email_from,
            } : {
              button_download_text: cellData.button_download_text,
              button_retake_text: cellData.button_retake_text,
              button_take_text: cellData.button_take_text,
              send_text: cellData.send_text,
              sent_text: cellData.sent_text,
              look_text: cellData.look_text,
              description_text: cellData.description_text,
              conditions1_text: cellData.conditions1_text,
              conditions2_text: cellData.conditions2_text,
              send_by_email_text: cellData.send_by_email_text,
              print_text: cellData.print_text,
              printed_text: cellData.printed_text,
              preparing_photo_text: cellData.preparing_photo_text,
              almost_there_text: cellData.almost_there_text,
              email_subject: cellData.email_subject,
              email_body: htmlLineBreaks(cellData.email_body),
              language_code: language.code,
              cell_id: cellData.id}) } })}
            disabled={mutationLoading}
          >
            {mutationLoading ? t('ui.save.short') : t('ui.update')}
          </button>

          <PhotoboothCellStatsButton cell={cellData} />
          <PhotoboothCellPhotosZipButton cell={cellData} />
        </>
      )}
    </Mutation>
  );
});

// sanitizeText côté back supprime \p{C} (donc les retours à la ligne) sur email_body, aussi bien
// sur la colonne que sur la traduction. Le corps étant injecté dans du HTML, on convertit avant
// l'envoi pour que le textarea se comporte normalement.
function htmlLineBreaks(text) {
  return typeof text === 'string' ? text.replace(/\r?\n/g, '<br>') : text;
}

function removeTypenameDeep(obj) {
  if (Array.isArray(obj)) {
    return obj.map(removeTypenameDeep);
  } else if (obj !== null && typeof obj === 'object') {
    const newObj = {};
    for (const key in obj) {
      if (key !== '__typename') {
        newObj[key] = removeTypenameDeep(obj[key]);
      }
    }
    return newObj;
  }
  return obj;
}

export default withRouter(PhotoboothOBSCellEditorWrapper);
